/* Room noise level behind the microphone gate. Pure logic (no Web Audio), fed one RMS value per analysed
   frame (about 60 per second).

   The floor is the quietest 5 % of the last ~8 s of input. A long window with a low percentile keeps the
   floor at the room's level while a string rings out; a short window with a high percentile (the original
   estimator) learned the note itself, raised the gate and cut sustained notes off within seconds.
   The window alone still learned any sound that went on for more than ~7.6 s, so a held "aaa", an "sss"
   or a 16-second siren disappeared mid-exercise. Frames loud enough for anything to hear them (over the
   lowest gate any listener uses, after the sensitivity setting) are therefore only sampled one in eight:
   a sound has to last about a minute before the floor reaches it, which is longer than anyone's breath,
   while a room that really got louder is still learned. While the app listens for chords (Jam, playing
   along with a song, chord exercises, the analyzer) the player strums for minutes on end, so loud frames
   are not sampled at all then (`hold`). A fresh window (a new microphone stream) takes every frame until
   its first estimate: that first second is the room, also when the room is louder than the old gate. */

/** Frames in the window (~8 s at 60 frames per second). */
const FRAMES = 480;
/** Loud frames enter the window only this rarely. */
const LOUD_EVERY = 8;
/** A fresh window takes every frame until its first estimate (~0.65 s). */
const FIRST = 40;
const MIN = 0.0015;
const MAX = 0.03;

/** Level threshold for a given floor before the user's sensitivity setting: a margin over the room. */
export const baseGate = (floor: number): number => Math.max(0.004, floor * 2.6);
/** The tuner's threshold before the sensitivity setting: a clear pitch stays measurable closer to the room,
    so it keeps reading a fading string (TunerPage.tsx). The lowest gate anything listens at. */
export const tunerGate = (floor: number): number => floor * 1.8;

export interface FloorInput {
  /** the user's sensitivity setting: every gate is divided by it (default 1) */
  sensitivity?: number;
  /** someone listens for chords: loud frames are the player, never the room */
  hold?: boolean;
}

export class NoiseFloor {
  /** estimated room noise level (RMS) */
  value = 0.003;
  private hist: number[] = [];
  private tick = 0;
  private loud = 0;

  /** Forgets the window (a new microphone stream) but keeps the last level as the starting point. */
  restart(): void {
    this.hist = [];
    this.tick = 0;
    this.loud = 0;
  }

  /** Adds one frame's RMS level and returns the floor. */
  push(rms: number, o: FloorInput = {}): number {
    const heard = Math.min(baseGate(this.value), tunerGate(this.value)) / (o.sensitivity || 1);
    if (this.hist.length >= FIRST && rms >= heard && (o.hold || this.loud++ % LOUD_EVERY !== 0)) return this.value;
    this.hist.push(rms);
    if (this.hist.length > FRAMES) this.hist.shift();
    if (this.hist.length >= 30 && ++this.tick % 10 === 0) {
      const s = this.hist.slice().sort((a, b) => a - b);
      const p = s[Math.floor(s.length * 0.05)] as number;
      this.value = Math.max(MIN, Math.min(MAX, p));
    }
    return this.value;
  }
}
